/**
 * Code QR lu a la camera (page d'activation d'une double authentification affichee sur un autre ecran) : chaque image
 * de la camera passe par le MEME lecteur que les captures (`readQrImage`), sur l'appareil ; aucune image n'est envoyee
 * ni gardee, ses pixels sont effaces des qu'ils ont servi, et la camera est rendue (pistes arretees) quoi qu'il arrive.
 *
 * Limite connue : le lecteur suppose une projection affine (pas de perspective) -- tenir le code bien en face.
 */
import { readQrImage, type QrReadResult } from './qr-decode.js';
import { rgbaLuminance } from './qr-image.js';

/** Pixels RGBA d'une image de la camera (`ImageData`), ou `null` si aucune image n'est encore prete. */
export type CameraFrame = { readonly width: number; readonly height: number; readonly data: Uint8ClampedArray } | null;

export interface CameraScanOptions {
  /** Annule la lecture (bouton « Annuler », editeur ferme, coffre verrouille). */
  readonly signal?: AbortSignal;
  /** Abandon apres ce delai sans code lisible. */
  readonly timeoutMs?: number;
  /** Intervalle entre deux images lues : le lecteur est pur JS, pas besoin de 60 images par seconde. */
  readonly intervalMs?: number;
  /** `navigator.mediaDevices` par defaut. */
  readonly mediaDevices?: Pick<MediaDevices, 'getUserMedia'>;
  /** Image courante de la camera ; par defaut, la video `preview` copiee sur un canevas. */
  readonly grabFrame?: (stream: MediaStream) => CameraFrame;
  /** Video qui montre la camera a l'utilisateur, et dont les images sont lues. */
  readonly preview?: HTMLVideoElement;
}

export const CAMERA_UNAVAILABLE = 'Aucune camera disponible sur cet appareil : choisissez ou collez une capture du code.';
export const CAMERA_DENIED = 'Acces a la camera refuse : autorisez-le dans le navigateur, ou choisissez une capture du code.';
export const CAMERA_TIMEOUT = 'Aucun code QR lisible a la camera : rapprochez-vous, tenez le code bien en face, ou choisissez une capture.';
export const CAMERA_CANCELLED = 'Lecture a la camera annulee.';

/** 60 s : le temps de trouver la page d'activation sur l'autre ecran. */
export const CAMERA_SCAN_TIMEOUT_MS = 60_000;
/** Plus que la taille d'une image HD : au-dela, l'image est reduite par le canevas. */
const MAX_FRAME_SIDE = 1280;

/** Le navigateur sait-il ouvrir une camera (contexte securise compris) ? Sinon le bouton n'est pas propose. */
export function cameraScanAvailable(scope: { navigator?: Navigator; isSecureContext?: boolean } = globalThis): boolean {
  return scope.isSecureContext !== false && typeof scope.navigator?.mediaDevices?.getUserMedia === 'function';
}

function videoFrameGrabber(video: HTMLVideoElement): (stream: MediaStream) => CameraFrame {
  const canvas = document.createElement('canvas');
  return () => {
    if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) return null;
    const scale = Math.min(1, MAX_FRAME_SIDE / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (context === null) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const frame = context.getImageData(0, 0, canvas.width, canvas.height);
    context.clearRect(0, 0, canvas.width, canvas.height);
    return frame;
  };
}

/**
 * Ouvre la camera arriere et lit ses images jusqu'au premier code QR lisible, a une annulation ou au delai. Rend le
 * texte lu (a valider par l'appelant : `totpFromQrText`), ou un probleme dit en clair, sans jamais citer le contenu.
 */
export async function scanQrWithCamera(options: CameraScanOptions = {}): Promise<QrReadResult> {
  const { signal, timeoutMs = CAMERA_SCAN_TIMEOUT_MS, intervalMs = 120, preview } = options;
  const mediaDevices = options.mediaDevices ?? (typeof navigator === 'undefined' ? undefined : navigator.mediaDevices);
  if (typeof mediaDevices?.getUserMedia !== 'function') return { ok: false, problem: CAMERA_UNAVAILABLE };
  if (signal?.aborted) return { ok: false, problem: CAMERA_CANCELLED };
  let stream: MediaStream;
  try {
    stream = await mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
  } catch (error) {
    const name = error instanceof Error ? error.name : '';
    return { ok: false, problem: name === 'NotAllowedError' || name === 'SecurityError' ? CAMERA_DENIED : CAMERA_UNAVAILABLE };
  }
  try {
    if (preview !== undefined) {
      try {
        preview.muted = true;
        preview.playsInline = true;
        preview.srcObject = stream;
      } catch {
        return { ok: false, problem: CAMERA_UNAVAILABLE };
      }
      await preview.play().catch(() => undefined);
    }
    const grab = options.grabFrame ?? (preview === undefined ? null : videoFrameGrabber(preview));
    if (grab === null) return { ok: false, problem: CAMERA_UNAVAILABLE };
    const deadline = Date.now() + timeoutMs;
    while (!signal?.aborted) {
      const frame = grab(stream);
      if (frame !== null) {
        const image = rgbaLuminance(frame);
        try {
          const result = readQrImage(image);
          if (result.ok) return result;
        } finally {
          image.luminance.fill(0);
        }
      }
      if (Date.now() >= deadline) return { ok: false, problem: CAMERA_TIMEOUT };
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, intervalMs);
        signal?.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
      });
    }
    return { ok: false, problem: CAMERA_CANCELLED };
  } finally {
    for (const track of stream.getTracks()) track.stop();
    if (preview !== undefined) preview.srcObject = null;
  }
}
